import indexeddb_wrapper from './indexeddb_wrapper';

type Function<T> = () => T | null | Promise<T | null>;

type StateInput<T> = {
   initial_value: T;
   mutate_value_fn?: (value: T, old_value: T) => T;
   on_change?: (value: T) => any;
   populate_fn?: Function<T>;
   index_key?: keyof T,
   state_name?: string;
   derive_value_fn?: (value: T) => T;
   db_name?: false | string;
};

type ElementOf<T> = T extends (infer E)[] ? E : never;

export type StateReturn<T> = {
   value: T;
   previous_value: T;
   get_stored_value: () => Promise<T | null>;
   populate_fn: () => Promise<T | null>;
   reset: () => void;
   init: () => void;
   indexeddb_crud: {
      get_one: (id: string) => Promise<T | null>;
      get_all: () => Promise<T[] | null>;
      set_one: (value: T) => Promise<void>;
      set_bulk: (array: T) => Promise<void>;
      clear_store: () => Promise<boolean>;
      remove_one: (id: string) => Promise<void>;
      filter: (filter_fn: (value: ElementOf<T>) => boolean) => Promise<T>
      paginate: (page: number, pageSize: number) => Promise<T>
      count: () => Promise<number>;
   }

   on_change: (call_back: (state: T, unsub: () => void) => any) => () => void;
   update_state: (callback: (value: T) => T | null) => void;
};

type StorageType = 'localStorage' | 'indexedDB' | 'capacitor';

let is_device_native = false;
let browser_storage: StorageType =
   typeof window === 'undefined' || !window?.indexedDB ? 'localStorage' : 'indexedDB';
let Preferences = { get: async (args: any) => ({ value: null }), set: async (args: any) => { } };

const DEFAULT_INDEX_KEYS = { last_updated_index: 'updated_at', main_index: 'id' };

export async function init_states_storage(storage: StorageType) {
   try {
      browser_storage = storage;
      // switch (storage) {
      //    case 'capacitor': {
      //       const [{ Capacitor }, { Preferences: PreferencesLib }] = await Promise.all([
      //          import('@capacitor/core'),
      //          import('@capacitor/preferences')
      //       ]);
      //       Preferences = PreferencesLib as any;
      //       is_device_native = Capacitor.isNativePlatform();
      //       if (is_device_native) browser_storage = 'capacitor';
      //       break;
      //    }
      //    default: {
      //    }
      // }
   } catch (error) {
      console.error('init_states_storage error: ', error);
   }
}

export function create_simple_state<T>(initial_value: T) {
   let current_state: T = $state(initial_value);
   return {
      get value() {
         return current_state;
      },
      set value(value: T) {
         current_state = value;
      }
   }


}

export function create_state<T>({
   state_name = '',
   initial_value,
   db_name = false,
   mutate_value_fn,
   populate_fn,
   on_change,
   index_key,
   derive_value_fn,
}: StateInput<T>): StateReturn<T> {
   let current_state = $state(initial_value);
   let previous_state = initial_value;
   let is_populated_state = $state(false);
   let is_populated_resolve: ((value: true | PromiseLike<true>) => void) | null = null

   async function await_state_init() {
      if (is_populated_state) return true
      return new Promise<true>((resolve) => {
         is_populated_resolve = resolve
      })
   }

   if (typeof window === 'undefined')
      return {
         value: current_state as T,
         get_stored_value: async () => null as T | null,
         populate_fn: async () => {
            return;
         }
      } as unknown as StateReturn<T>;

   async function get_stored_value(): Promise<T | null> {
      try {
         if (!state_name || !db_name) return null;

         let stored_value: T | null = null;
         if (is_device_native) stored_value = await get_capacitor_store<T>(state_name);
         else if (browser_storage === 'indexedDB') {
            // console.log('from create_state.svelte.ts: ', db_name, state_name)

            stored_value = await get_indexed_db_store<T>(db_name, state_name);
         } else stored_value = get_local_storage_store<T>(state_name);

         if (!stored_value) return null;

         return stored_value;
      } catch (error) {
         console.error(`Error at get_value function, state: ${state_name}.`, { error });
         return null;
      } finally {
         is_populated_state = true
         is_populated_resolve?.(true)
      }
   }

   if (db_name)
      get_stored_value()
         .then((value) => {
            if (!value) return;
            previous_state = current_state;
            current_state = derive_value_fn ? derive_value_fn(value) : value;
         })
         .catch((error) => console.error('error at get_stored_value .catch', { error }));

   async function populate_fn_handler() {
      if (!populate_fn) return null;
      if (db_name) await await_state_init()
      const value = await populate_fn();
      if (!value) return null;
      set_value(value);
      return value;
   }

   function update_state(callback: (value: T) => T | null) {
      const updated_value = callback(current_state);
      if (!updated_value) return;
      set_value(updated_value);
   }

   function set_value(value: T) {
      const new_value = mutate_value_fn ? mutate_value_fn(value, current_state) : value;
      previous_state = current_state;

      const derived_value = derive_value_fn ? derive_value_fn(new_value) : new_value;
      current_state = derived_value
      if (on_change) on_change(derived_value);
      if (db_name) persistStore(db_name, state_name, derived_value, browser_storage);
   }

   function reset_state() {
      previous_state = current_state;
      current_state = initial_value

      if (on_change) on_change(initial_value);
      if (db_name) resetPersistedStore(db_name, state_name, initial_value, browser_storage);
   }

   const { on_change: _on_change, on_change_handler } = create_on_change_handler<T>();

   const indexeddb_crud: StateReturn<T>['indexeddb_crud'] = {
      get_one: async (id: string) => {
         if (browser_storage !== 'indexedDB') return null
         return indexeddb_wrapper.get_one(db_name || '', state_name, id, DEFAULT_INDEX_KEYS)
      },
      get_all: async () => {
         if (browser_storage !== 'indexedDB') return []
         return indexeddb_wrapper.get_all<T>(db_name || '', state_name, DEFAULT_INDEX_KEYS)
      },
      set_one: async (value: T) => {
         if (browser_storage !== 'indexedDB') return
         await indexeddb_wrapper.set_one(db_name || '', state_name, value, DEFAULT_INDEX_KEYS)
      },
      set_bulk: async (array: T) => {
         if (browser_storage !== 'indexedDB' || !Array.isArray(array)) return
         await indexeddb_wrapper.set_bulk(db_name || '', state_name, array, DEFAULT_INDEX_KEYS)
      },
      clear_store: async () => {
         if (browser_storage !== 'indexedDB') return false
         return indexeddb_wrapper.clear_store(db_name || '', state_name, DEFAULT_INDEX_KEYS)
      },
      remove_one: async (id: string) => {
         if (browser_storage !== 'indexedDB') return
         await indexeddb_wrapper.remove_one(db_name || '', state_name, id, DEFAULT_INDEX_KEYS)
      },
      filter: async (filter_fn: (value: ElementOf<T>) => boolean) => {
         if (browser_storage !== 'indexedDB') return [] as T
         return indexeddb_wrapper.filter(db_name || '', state_name, filter_fn, DEFAULT_INDEX_KEYS) as T
      },
      paginate: async (page: number, pageSize: number) => {
         if (browser_storage !== 'indexedDB') return [] as T
         return indexeddb_wrapper.paginate<T, typeof DEFAULT_INDEX_KEYS>(db_name || '', state_name, page, pageSize, DEFAULT_INDEX_KEYS, DEFAULT_INDEX_KEYS.main_index) as T
      },
      count: async () => {
         if (browser_storage !== 'indexedDB') return 0
         return indexeddb_wrapper.count(db_name || '', state_name, DEFAULT_INDEX_KEYS)
      },
   }

   return {
      get value() {
         return current_state;
      },
      indexeddb_crud,

      set value(value: T) {
         set_value(value);
         on_change_handler(value);
      },
      get previous_value() {
         return previous_state;
      },
      reset: reset_state,
      get_stored_value: get_stored_value,
      init: () => { },
      on_change: _on_change,
      populate_fn: populate_fn_handler,
      update_state
   };
}


async function resetPersistedStore<T>(db_name: string, store_name: string, value: T, browser_storage: StorageType) {
   if (typeof window === 'undefined') return;
   if (is_device_native) await set_capacitor_store<T>({ key: store_name, value: value ?? null as T });
   else if (browser_storage === 'indexedDB') await indexeddb_wrapper.clear_store(db_name, store_name, DEFAULT_INDEX_KEYS);
   else setLocalStorageStore<T>({ key: store_name, value: value ?? null as T });
}

async function persistStore<T>(db_name: string, store_name: string, value: T, browser_storage: StorageType) {
   if (typeof window === 'undefined' || value === undefined) return;

   if (is_device_native) await set_capacitor_store<T>({ key: store_name, value });
   else if (browser_storage === 'indexedDB') await setIndexedDBStore<T>(db_name, store_name, value);
   else setLocalStorageStore<T>({ key: store_name, value });
}


async function get_indexed_db_store<T>(db_name: string, store_name: string): Promise<T | null> {
   const document = await indexeddb_wrapper.get_all(db_name, store_name, DEFAULT_INDEX_KEYS) as T | null;
   // console.log({ document })
   if (!document) return null;
   return document;
}

async function setIndexedDBStore<T>(db_name: string, store_name: string, value: T) {
   // const insert_document = { id: key, value: $state.snapshot(value) || null } as { id: string, value: T };
   console.log({ type: 'set', store: store_name, value });
   if (Array.isArray(value)) indexeddb_wrapper.set_bulk<T>(db_name, store_name, $state.snapshot(value) as T[], DEFAULT_INDEX_KEYS);
   else indexeddb_wrapper.set_one<T>(db_name, store_name, $state.snapshot(value) as T, DEFAULT_INDEX_KEYS)
   return
}

function get_local_storage_store<T>(key: string): T | null {
   try {
      if (typeof window === 'undefined') return null;
      const string_value = localStorage.getItem(key);
      if (!string_value) return null;

      const value = JSON.parse(string_value) as T | null;

      return value;
   } catch (error) {
      // console.error(`Error at get_local_storage_store function, key: ${key}.`, { error });
      return null;
   }
}

function setLocalStorageStore<T>({ key, value }: { key: string; value: T }) {
   try {
      if (typeof window === 'undefined') return;
      localStorage.setItem(key, JSON.stringify(value));
   } catch (error) {
      // console.error(`Error at setLocalStorageStore function, key: ${key}.`, { error });
   }
}

async function get_capacitor_store<T>(key: string): Promise<T | null> {
   try {
      if (typeof window === 'undefined' || !is_device_native) return null;

      const result = await Preferences.get({ key });
      const string_value = result.value;
      if (!string_value) return null;

      const value = JSON.parse(string_value) as T | null;
      return value;
   } catch (error) {
      // console.error(`Error at get_capacitor_store function, key: ${key}.`, { error });
      return null;
   }
}

async function set_capacitor_store<T>({ key, value }: { key: string; value: T }) {
   try {
      if (typeof window === 'undefined' || !is_device_native) return;
      await Preferences.set({ key, value: JSON.stringify(value) });
   } catch (error) {
      // console.error(`Error at set_capacitor_store function, key: ${key}.`, { error });
   }
}

function create_on_change_handler<T>() {
   const on_change_fns_map = new Map<string, (value: T, unsub: () => void) => any>();

   return {
      on_change: (call_back: (value: T, unsub: () => void) => any, destroy_delay?: number) => {
         const id = random_string();

         on_change_fns_map.set(id, call_back);

         const unsub = () => on_change_fns_map.delete(id);

         if (destroy_delay) setTimeout(unsub, destroy_delay);
         return () => {
            unsub();
         };
      },
      on_change_handler: (value: T) => {
         on_change_fns_map.forEach((callback, id) =>
            callback?.(value, () => on_change_fns_map.delete(id))
         );
      }
   };
}

const characters = 'abcdefghijklmnopqrstuvwxyz';
function random_string(length = 10) {
   let result = '';
   const charactersLength = characters.length;
   for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
   }
   return result;
}
